/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/prop-types */
/*
 * Media Library Header component
 *
 * @param {string} folderName: the name of the folder in view
 *
 */
import {useState, useRef, useEffect} from 'react';
import Util from '../util/Util';
import Locale from '../util/Locale';
import MarsUpload from './MarsUpload';
import Rename from './Rename';
import FilterMenu from './FilterMenu';
import DateRangeFilter from './DateRangeFilter';
import Delete from './Delete';
import ViewSegmentedButton from './ViewSegmentedButton';
import ExtrasMenu from './ExtrasMenu';
import './Header.scss';
import Analytics from '../util/Analytics';

function Header({
    folderName,
    controllerApi,
    filterSelect,
    focused,
    isTagView,
    location,
    getAssetList,
    getLocked,
    getSortValue,
    getDateRange,
    gridView,
    isDraggingAsset,
    movementSrc,
    onDeleteAssetList,
    onEditAsset,
    onFilterSelect,
    onSearchQueryChange,
    onSortChange,
    onDateRangeChange,
    getSelectedPathForUploads,
    onViewChange,
    selections,
    setMovementSrc,
    setShowModal,
    toggleSidebar,
    viewData,
    assetFilters,
    totalAssets,
    page,
    size
}) {
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [showExtrasMenu, setShowExtrasMenu] = useState(false);
    const [hasCopied, setHasCopied] = useState(false);
    const [extrasPositionX, setExtrasPositionX] = useState(0);
    const [filterPositionX, setFilterPositionX] = useState(0);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [debounceRequest, setDebounceRequest] = useState(false);
    const searchDebounceDelayTimer = useRef(null);
    let lastQuery = null;

    const autoFocusSearch = input => {
        if (input && 'focus' in input) {
            setTimeout(() => {
                input.focus();
            }, 0);
        }
    };

    const calculateCurrentIndex = (focusedIndex, currentPage, pageSize) => {
        return focusedIndex + pageSize * currentPage + 1;
    };

    const onViewToggle = () => {
        if (onViewChange) {
            onViewChange(!gridView);
        }
    };

    const onMobileSearch = () => {
        setShowMobileSearch(true);
    };

    const onSearch = event => {
        if (event.target) {
            setSearchText(event.target.value);
            setDebounceRequest(true);
        }
    };

    const onSearchKeyPress = event => {
        if (event.key === 'Enter') {
            if (searchDebounceDelayTimer.current) {
                clearTimeout(searchDebounceDelayTimer.current);
            }
            const currentQuery = encodeURIComponent(searchText.trim());
            if (currentQuery !== lastQuery) {
                lastQuery = currentQuery;
                if (onSearchQueryChange) {
                    onSearchQueryChange(currentQuery);
                }
            }
        }
    };

    const onFilterSelectHandler = value => {
        setShowFilterMenu(false);
        if (onFilterSelect) {
            onFilterSelect(value);
            Analytics.trackEvent('Filter', `Select (${value})`);
        }
    };

    const onExtrasMenuClose = () => {
        setShowExtrasMenu(false);
    };

    const onSortChangeFromMenu = event => {
        onExtrasMenuClose();
        onSortChange(event.target.value);
    };

    const onSortChangeHandler = event => {
        if (onSortChange) {
            onSortChange(event.target.value);
            Analytics.trackEvent('Sort', `Change (${event.target.value})`);
        }
    };

    const onDeleteAssetListHandler = () => {
        if (!selections || !viewData) {
            return;
        }
        const deleteAssetsList = [];
        selections.forEach((isSelected, idx) => {
            if (isSelected) {
                deleteAssetsList.push(viewData[idx]);
            }
        });
        if (deleteAssetsList.length && onDeleteAssetList) {
            Analytics.trackEvent(deleteAssetsList.length > 1 ? 'Asset(s)' : 'Asset', 'Delete (Modal)');
            setShowModal({
                content: (
                    <Delete
                        title={
                            deleteAssetsList.length > 1 ? Locale.getText('DELETE_FILES') : Locale.getText('DELETE_FILE')
                        }
                        list={deleteAssetsList}
                        setShowModal={setShowModal}
                        onAction={onDeleteAssetList}
                    />
                )
            });
        }
    };

    const toggleCheckIcon = () => {
        setHasCopied(true);
        setTimeout(() => {
            setHasCopied(false);
        }, 1000);
    };

    const openNewTab = () => {
        if (focused.name) {
            window.open(Util.getVideoAssetLink(focused.name));
            Analytics.trackEvent('Asset', 'Open in new Tab');
        }
    };

    const showExtras = event => {
        const rect = event.currentTarget.getBoundingClientRect();
        setExtrasPositionX((rect.left + rect.right) / 2);
        setShowExtrasMenu(true);
    };

    const showFilters = event => {
        const rect = event.currentTarget.getBoundingClientRect();
        setFilterPositionX((rect.left + rect.right) / 2);
        setShowFilterMenu(true);
    };

    const openDateRangeFromFilter = () => {
        // Opened from the filter menu's "Date Range" row: close the filter menu and open the
        // date modal in its place, reusing the filter button's position (filterPositionX).
        setShowFilterMenu(false);
        setShowDateFilter(true);
    };

    const onDateRangeSave = (from, to) => {
        if (onDateRangeChange) {
            onDateRangeChange(from, to);
            Analytics.trackEvent('DateFilter', 'Save');
        }
    };

    const onDateRangeClear = () => {
        if (onDateRangeChange) {
            onDateRangeChange(null, null);
            Analytics.trackEvent('DateFilter', 'Clear');
        }
    };

    const closeMobileSearch = () => {
        setShowMobileSearch(false);
        setSearchText('');
        onSearchKeyPress({key: 'Enter'});
    };

    const startSearchDebounceDelay = () => {
        if (searchDebounceDelayTimer.current) {
            clearTimeout(searchDebounceDelayTimer.current);
        }
        searchDebounceDelayTimer.current = setTimeout(() => {
            onSearchKeyPress({key: 'Enter'});
        }, 750);
    };

    useEffect(() => {
        if (debounceRequest) {
            startSearchDebounceDelay();
            setDebounceRequest(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounceRequest]);

    const isMovementMode = () => {
        return !isTagView && selections && selections.length > 0 ? 'movement-mode' : '';
    };

    const renderMovementActions = () => {
        return <div className="header-movement-actions" />;
    };

    const renderMoveButton = () => {
        if (
            getLocked &&
            !getLocked() &&
            controllerApi &&
            !controllerApi.isAdvertisingRoot() &&
            controllerApi.isShowingTags()
        ) {
            const nSelections = selections ? selections.filter(flag => flag).length : 0;
            if (nSelections) {
                const text = nSelections > 1 ? <div className="withText">({nSelections})</div> : '';
                const content = (
                    <div className="buttonWithIcon">
                        <div className="svgBlock">{Util.getMoveIcon('#FFFFFF')}</div>
                        {text}
                    </div>
                );
                return (
                    <nsemble-button
                        primary
                        class="move-button"
                        title={Locale.getText('MOVE_ASSETS')}
                        onClick={() => {
                            const moveList = getAssetList();
                            toggleSidebar();
                            Analytics.trackEvent(moveList.length > 1 ? 'Asset(s)' : 'Asset', 'Move (Pending)');
                            setMovementSrc(moveList);
                        }}
                    >
                        {content}
                    </nsemble-button>
                );
            }
        }
        return null;
    };

    const renderDeleteButton = () => {
        if (getLocked && !getLocked() && onDeleteAssetList) {
            const nSelections = selections ? selections.filter(flag => flag).length : 0;
            if (nSelections) {
                const text = nSelections > 1 ? <div className="withText">({nSelections})</div> : '';
                const content = (
                    <div className="buttonWithIcon">
                        <div className="svgBlock">{Util.getDeleteIcon()}</div>
                        {text}
                    </div>
                );
                return (
                    <nsemble-button
                        destructive
                        class="delete-button"
                        onClick={onDeleteAssetListHandler}
                        title={Locale.getText('DELETE_ASSETS')}
                    >
                        {content}
                    </nsemble-button>
                );
            }
        }
        return null;
    };

    const renderFilterButton = (withText = false) => {
        const {dateFrom, dateTo} = getDateRange ? getDateRange() : {dateFrom: null, dateTo: null};
        const hasDateRange = Boolean(dateFrom && dateTo);
        const isFiltered = filterSelect !== 'all' || hasDateRange;
        return (
            <nsemble-button
                class={isFiltered ? 'filter-button is-filtered' : 'filter-button'}
                onClick={showFilters}
                title={Locale.getText('FILTER_ASSETS')}
            >
                <div className="icon-text">
                    <div className="svgBlock">
                        <svg
                            className={isFiltered ? 'color-filtered' : 'color-notfiltered'}
                            xmlns="http://www.w3.org/2000/svg"
                            x="0px"
                            y="0px"
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                        >
                            <path
                                stroke="none"
                                d="M9,19c-0.6,0-1-0.4-1-1s0.4-1,1-1h6c0.6,0,1,0.4,1,1s-0.4,1-1,1H9z M6,13c-0.6,0-1-0.4-1-1s0.4-1,1-1h12 c0.6,0,1,0.4,1,1s-0.4,1-1,1H6z M3,7C2.4,7,2,6.6,2,6s0.4-1,1-1h18c0.6,0,1,0.4,1,1s-0.4,1-1,1H3z"
                            />
                        </svg>
                    </div>
                    {withText && (
                        <div className={isFiltered ? 'textBlock color-filtered' : 'textBlock'}>
                            {Locale.getText('FILTER')}
                        </div>
                    )}
                </div>
            </nsemble-button>
        );
    };

    const renderExtrasButton = () => {
        return (
            <nsemble-button class="extras-button" onClick={showExtras} title={Locale.getText('MORE_CONTROLS')}>
                <div className="svgBlock">{Util.getExtrasIcon('#00528A')}</div>
            </nsemble-button>
        );
    };

    const renderSortSelector = inputHandler => {
        const value = getSortValue ? getSortValue() : 'nameAsc';
        const sortByMap = Util.sortByMap();
        return (
            <nsemble-select
                value={value}
                class="sort-selector"
                onInput={inputHandler}
                title={Locale.getText('SORT_ASSETS')}
            >
                {Object.keys(sortByMap).map(key => (
                    <option value={key} key={key}>
                        {sortByMap[key]}
                    </option>
                ))}
            </nsemble-select>
        );
    };

    const renderSortControl = () => {
        return (
            <div className="sort-control">
                <nsemble-button text class="sort-icon">
                    <div className="svgBlock">{Util.getSortIcon()}</div>
                </nsemble-button>
                {renderSortSelector(onSortChangeHandler)}
            </div>
        );
    };

    const renderUploadButton = (useLongerDescription = false) => {
        if ((getLocked && getLocked()) || !getSelectedPathForUploads) {
            return null;
        }
        return (
            <nsemble-button
                class="upload-button"
                text
                title={Locale.getText('UPLOAD_ASSETS')}
                onClick={() => {
                    setShowModal({
                        content: (
                            <MarsUpload
                                title={Locale.getText('UPLOAD')}
                                setShowUpload={setShowModal}
                                getSelectedPathForUploads={getSelectedPathForUploads}
                                advertising={controllerApi.isAdvertisingRoot()}
                            />
                        )
                    });
                }}
            >
                <div className="icon-text">
                    <div className="svgBlock">{Util.getUploadIcon('#00528A')}</div>
                    <div className="textBlock">
                        {useLongerDescription ? Locale.getText('UPLOAD_MEDIA') : Locale.getText('UPLOAD')}
                    </div>
                </div>
            </nsemble-button>
        );
    };

    const renderExtrasMenu = () => {
        if (showExtrasMenu) {
            if (focused) {
                return (
                    <ExtrasMenu x={extrasPositionX} onClose={onExtrasMenuClose}>
                        {!getLocked ||
                            (getLocked && !getLocked() && (
                                <nsemble-button
                                    text
                                    onClick={() =>
                                        setShowModal &&
                                        setShowModal({
                                            content: (
                                                <Rename
                                                    title={Locale.getText('EDIT_ASSET')}
                                                    asset={focused}
                                                    onClose={setShowModal}
                                                    onEditAsset={onEditAsset}
                                                />
                                            )
                                        })
                                    }
                                >
                                    <div className="icon-text">
                                        <div className="svgBlock">{Util.getEditIcon('#00528A')}</div>
                                        <div className="textBlock">{Locale.getText('EDIT_ASSET')}</div>
                                    </div>
                                </nsemble-button>
                            ))}
                        <nsemble-button
                            text
                            onClick={() => {
                                Util.copyToClipboard(focused, toggleCheckIcon);
                            }}
                        >
                            <div className="icon-text">
                                <div className="svgBlock">{Util.getClipboardCopyIcon('#00528A')}</div>
                                <div className="textBlock">{Locale.getText('COPY_LINK_ASSET')}</div>
                            </div>
                        </nsemble-button>
                        <nsemble-button onClick={openNewTab} text>
                            <div className="icon-text">
                                <div className="svgBlock">{Util.getOpenNewTabIcon('#00528A')}</div>
                                <div className="textBlock">{Locale.getText('OPEN_TAB')}</div>
                            </div>
                        </nsemble-button>
                    </ExtrasMenu>
                );
            }
            return (
                <ExtrasMenu x={extrasPositionX} onClose={onExtrasMenuClose}>
                    <ViewSegmentedButton active={gridView} onClick={onViewToggle} />
                    <nsemble-button
                        text
                        class="mobile-search-button"
                        onClick={onMobileSearch}
                        title={Locale.getText('FIND_ASSETS')}
                    >
                        <div className="icon-text">
                            <div className="svgBlock">{Util.getSearchIcon('#00528A')}</div>
                            <div className="textBlock">{Locale.getText('SEARCH')}</div>
                        </div>
                    </nsemble-button>
                    {renderFilterButton(true)}
                    <div className="sort-from-menu extra-menu-no-close">
                        <nsemble-button
                            text
                            class="mobile-sort-button"
                            onClick={onSortChangeHandler}
                            title={Locale.getText('SORT_ASSETS')}
                        >
                            <div className="icon-text">
                                <div className="svgBlock">{Util.getSortIcon()}</div>
                                <div className="textBlock">{Locale.getText('SORT')}</div>
                            </div>
                        </nsemble-button>
                        {renderSortSelector(onSortChangeFromMenu)}
                    </div>
                    {renderUploadButton(true)}
                </ExtrasMenu>
            );
        }
        return null;
    };

    const renderFilterMenu = () => {
        const showAssets = assetFilters ? assetFilters.join(',') : '';
        return (
            <FilterMenu
                select={filterSelect}
                onSelect={onFilterSelectHandler}
                x={filterPositionX}
                showAssets={showAssets}
                onDateRangeClick={openDateRangeFromFilter}
            />
        );
    };

    const renderDateFilterMenu = () => {
        const {dateFrom, dateTo} = getDateRange ? getDateRange() : {dateFrom: null, dateTo: null};
        return (
            <DateRangeFilter
                x={filterPositionX}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onSave={onDateRangeSave}
                onClear={onDateRangeClear}
                onClose={() => setShowDateFilter(false)}
                onBack={() => {
                    // Close X: return to the filter menu it was opened from.
                    setShowDateFilter(false);
                    setShowFilterMenu(true);
                }}
            />
        );
    };

    const renderSearchInput = (classname, autofocus = false) => {
        return (
            <nsemble-input
                onInput={onSearch}
                onKeyPress={onSearchKeyPress}
                variant
                prefix="search"
                name="search"
                value={searchText}
                class={classname}
                placeholder={Locale.getText('FIND_ASSET_BY_NAME')}
                ref={autofocus ? autoFocusSearch : () => {}}
                title={Locale.getText('FIND_ASSETS')}
            />
        );
    };

    const renderStandardActions = () => {
        return (
            <div className="header-standard-actions">
                {renderDeleteButton()}
                {renderMoveButton()}
                {renderSearchInput('desktop-search')}
                <nsemble-button
                    text
                    class="mobile-search-button"
                    onClick={onMobileSearch}
                    title={Locale.getText('FIND_ASSETS')}
                >
                    <div className="svgBlock">{Util.getSearchIcon('#00528A')}</div>
                </nsemble-button>
                {renderFilterButton()}
                {renderSortControl()}
                {showFilterMenu && renderFilterMenu()}
                {showDateFilter && renderDateFilterMenu()}
                {renderUploadButton()}
                <ViewSegmentedButton active={gridView} onClick={onViewToggle} />
                {renderExtrasButton()}
                {renderExtrasMenu()}
            </div>
        );
    };

    const renderAssetCount = () => {
        let focusedIndex = -1;
        if (focused) {
            if (Util.isValidAssetId(focused.id)) {
                focusedIndex = viewData.findIndex(child => child.id === focused.id);
            } else {
                focusedIndex = focused.index; // stock images don't have an id
            }
        }

        if (focusedIndex !== -1) {
            return (
                <div className="detail-view-counts">
                    <span>{calculateCurrentIndex(focusedIndex, page, size)}</span>
                    <span>{Locale.getText('OF')}</span>
                    <span>{totalAssets}</span>
                </div>
            );
        }
        return null;
    };

    const isLocked = getLocked && getLocked();
    if (focused) {
        return (
            <div className={`media-library-header ${isMovementMode()}`}>
                <div className="detail-name-with-edit">
                    <div className="media-library-detail-name">{Util.decodeLabel(focused.label)}</div>
                    {isLocked && <span className="appendLockIcon">{Util.getLockIcon()}</span>}
                    {!isLocked && (
                        <nsemble-button
                            class="rename-button"
                            text
                            title={Locale.getText('EDIT_ASSET')}
                            onClick={() =>
                                setShowModal &&
                                setShowModal({
                                    content: (
                                        <Rename
                                            title={Locale.getText('EDIT_ASSET')}
                                            asset={focused}
                                            onClose={setShowModal}
                                            onEditAsset={onEditAsset}
                                        />
                                    )
                                })
                            }
                        >
                            <div className="svgBlock">{Util.getEditIcon('#00528A')}</div>
                        </nsemble-button>
                    )}
                </div>
                {focused && viewData && renderAssetCount()}
                <div className="media-library-detail-buttons">
                    <nsemble-button
                        text
                        title={Locale.getText('COPY_LINK_ASSET')}
                        onClick={() => {
                            Util.copyToClipboard(focused, toggleCheckIcon);
                        }}
                        class={`detail-copy-link ${hasCopied && 'link-copied'}`}
                    >
                        {hasCopied ? Locale.getText('COPIED') : Locale.getText('COPY_LINK_ASSET')}
                    </nsemble-button>
                    <nsemble-button
                        onClick={openNewTab}
                        text
                        class="detail-open-tab"
                        title={Locale.getText('OPEN_TAB')}
                    >
                        {Locale.getText('OPEN_TAB')}
                    </nsemble-button>
                </div>
                {renderExtrasButton()}
                {renderExtrasMenu()}
            </div>
        );
    }

    if (showMobileSearch) {
        return (
            <div className={`media-library-header ${isMovementMode()}`}>
                {renderDeleteButton()}
                {renderMoveButton()}
                {renderSearchInput('full-search', true)}
                {renderFilterButton()}
                {showFilterMenu && renderFilterMenu()}
                {showDateFilter && renderDateFilterMenu()}
                <nsemble-button text onClick={closeMobileSearch}>
                    <div className="svgBlock">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                            <g
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                fill="#00528A"
                                stroke="#00528A"
                            >
                                <line
                                    fill="none"
                                    stroke="#00528A"
                                    strokeMiterlimit="10"
                                    x1="19"
                                    y1="5"
                                    x2="5"
                                    y2="19"
                                />{' '}
                                <line
                                    fill="none"
                                    stroke="#00528A"
                                    strokeMiterlimit="10"
                                    x1="19"
                                    y1="19"
                                    x2="5"
                                    y2="5"
                                />
                            </g>
                        </svg>
                    </div>
                </nsemble-button>
            </div>
        );
    }
    return (
        <div className={`media-library-header ${isMovementMode()}`}>
            <div className="desktop-folder-name">
                {folderName}
                {isLocked && <span className="appendLockIcon">{Util.getLockIcon()}</span>}
            </div>
            <div className="mobile-path-location" onClick={toggleSidebar}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                    <g
                        className="nc-icon-wrapper"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        fill="#00528A"
                        stroke="#00528A"
                    >
                        <polyline
                            fill="none"
                            stroke="#00528A"
                            strokeMiterlimit="10"
                            points="17,2 7,12 17,22 "
                            transform="translate(0, 0)"
                        />
                    </g>
                </svg>
                <span className={isTagView ? 'breadcrumbs ltr' : 'breadcrumbs'}>{location}</span>
                {isLocked && <span className="appendLockIcon">{Util.getLockIcon()}</span>}
            </div>
            {movementSrc && !isDraggingAsset ? renderMovementActions() : renderStandardActions()}
        </div>
    );
}

export default Header;
